import React, { useState } from "react";
import useDestinationLinkDialogStep, {
  DestinationLinkDialogStep,
} from "./hooks/useDestinationLinkDialogStep";
import DestinationStep from "./components/IntegrationStep/DestinationStep";
import ConnectDestinationStep from "./components/ConnectDestinationStep/ConnectDestinationStep";
import ErrorStep from "./components/ErrorStep";
import SuccessStep from "./components/SuccessStep";
import { Destination } from "../../../../../models/Entities";
import MergeLinkDialog from "../../../../common/MergeLinkDialog";

const { INTEGRATION_STEP, CONNECT_DESTINATION_STEP, SUCCESS_STEP, ERROR_STEP } =
  DestinationLinkDialogStep;

type DestinationLinkDialogProps = {
  open: boolean;
  onClose: () => void;
  refetchDestinations: () => void;
};

/**
 * Merge Link like dialog for creating a SyncConnection.
 *
 * For now, will only support creating a destination, but long term plan is to allow
 * creating both Source and Destinations from this modal. Therefore, all code
 * for this dialog will support both connection-types.
 */
const DestinationLinkDialog = ({
  open,
  onClose: propsOnClose,
  refetchDestinations,
}: DestinationLinkDialogProps) => {
  // state
  const { step, setStep } = useDestinationLinkDialogStep();
  const [destination, setDestination] = useState<Destination>();
  const [error, setError] = useState<{ message: string; third_party_error: boolean }>();

  // here so that they do not delete when going between steps
  const [authFields, setAuthFields] = useState<{ [key: string]: string }>({});

  // event handlers
  const onDestinationSelect = (newDestination: Destination) => {
    // clear AuthFields when switching integration
    if (newDestination.id !== destination?.id) {
      setAuthFields({});
    }

    setDestination(newDestination);
    setStep(DestinationLinkDialogStep.CONNECT_DESTINATION_STEP);
  };

  const onClose = () => {
    // reset to initial state
    setStep(DestinationLinkDialogStep.INTEGRATION_STEP);
    setDestination(undefined);
    setError(undefined);
    setAuthFields({});

    propsOnClose();
  };

  const onNext = (error?: { message: string; third_party_error: boolean }) => {
    if (error) {
      setError(error);
      setStep(DestinationLinkDialogStep.ERROR_STEP);
    } else {
      setStep(DestinationLinkDialogStep.SUCCESS_STEP);
    }
  };

  return (
    <MergeLinkDialog
      open={open}
      onClose={onClose}
      size={step === DestinationLinkDialogStep.INTEGRATION_STEP ? "sm" : "lg"}
    >
      {step === DestinationLinkDialogStep.INTEGRATION_STEP && (
        <DestinationStep onClose={onClose} onDestinationSelect={onDestinationSelect} />
      )}
      {step === DestinationLinkDialogStep.CONNECT_DESTINATION_STEP && (
        <ConnectDestinationStep
          destination={destination!}
          onClose={onClose}
          onBack={() => setStep(DestinationLinkDialogStep.INTEGRATION_STEP)}
          onNext={onNext}
          authFields={authFields}
          setAuthFields={setAuthFields}
        />
      )}
      {step === SUCCESS_STEP && (
        <SuccessStep
          destination={destination!}
          onClose={() => {
            refetchDestinations();
            onClose();
          }}
          onBack={() => setStep(DestinationLinkDialogStep.CONNECT_DESTINATION_STEP)}
        />
      )}
      {step === DestinationLinkDialogStep.ERROR_STEP && (
        <ErrorStep
          destination={destination!}
          onClose={onClose}
          onBack={() => setStep(DestinationLinkDialogStep.CONNECT_DESTINATION_STEP)}
          error={error!}
        />
      )}
    </MergeLinkDialog>
  );
};

export default DestinationLinkDialog;
